import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Check, CheckCheck, ChevronDown, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import type { NotificationRow } from "@/admin/hooks/use-notifications";

/**
 * Collapsible, per-record notifications section.
 *
 * Renders the current user's UNREAD notifications for a record and keeps them
 * visible until they are explicitly marked read or dismissed - the section
 * never clears them just because the record was opened. Read notifications are
 * kept out of the way behind an expand/collapse toggle so the unread ones stay
 * the focus.
 *
 * Purely presentational: read state is owned by the notification
 * infrastructure. Callers wire the mark-read / mark-all-read / dismiss handlers
 * to the `useNotificationItemAction` / `useMarkNotificationsRead` mutations.
 */
export interface NotificationsPanelProps {
  /** Unread notifications for the record (always shown). */
  unread: NotificationRow[];
  /** Read notifications for the record (shown when the panel is expanded). */
  read?: NotificationRow[];
  onMarkRead: (uuid: string) => void;
  onMarkAllRead: () => void;
  onDismiss: (uuid: string) => void;
  /** Optional contextual action (e.g. a "View" button) per notification. */
  renderItemAction?: (notification: NotificationRow) => ReactNode;
  markAllPending?: boolean;
  /** Optional: called when the read-history section is expanded. */
  onExpandHistory?: () => void;
  className?: string;
}

function levelClasses(level: NotificationRow["level"], muted: boolean): string {
  if (muted) {
    return "border-[var(--rule)] bg-[var(--bg-light)]";
  }
  if (level === "critical") {
    return "border-[var(--status-error)] bg-[var(--status-error-bg-subtle)]";
  }
  if (level === "info") {
    return "border-[var(--rule-strong)] bg-[var(--bg-subtle)]";
  }
  return "border-[var(--status-warn)] bg-[var(--status-warn-bg)]";
}

function NotificationItem({
  notification,
  muted,
  action,
  onMarkRead,
  onDismiss,
}: {
  notification: NotificationRow;
  muted: boolean;
  action?: ReactNode;
  onMarkRead?: (uuid: string) => void;
  onDismiss: (uuid: string) => void;
}) {
  const { t } = useTranslation();
  const repeats = muted ? 1 : (notification.event_count ?? 1);
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-[6px] border-[0.5px] px-4 py-2.5",
        levelClasses(notification.level, muted),
      )}
    >
      <div className="min-w-0">
        <p
          className={cn(
            "text-[11px] font-medium",
            muted ? "text-[var(--ink-2)]" : "text-[var(--ink)]",
          )}
        >
          {notification.title}
          {repeats > 1 && (
            <span className="ml-1.5 font-mono text-[10px] font-medium tabular-nums text-[var(--ink-2)]">
              {t("notifications.newCount", {
                count: repeats,
                defaultValue: "{{count}} new",
              })}
            </span>
          )}
        </p>
        {notification.body && (
          <p className="mt-0.5 line-clamp-2 text-[11px] text-[var(--ink-2)]">
            {notification.body}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {action}
        {onMarkRead && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => onMarkRead(notification.uuid)}
            title="Mark as read"
            className="cursor-pointer gap-1"
          >
            <Check className="h-3 w-3" />
            Mark read
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => onDismiss(notification.uuid)}
          title="Dismiss"
          aria-label="Dismiss"
          className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function NotificationsPanel({
  unread,
  read = [],
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  renderItemAction,
  markAllPending,
  onExpandHistory,
  className,
}: NotificationsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const unreadCount = unread.length;
  const hasHistory = read.length > 0;

  // Nothing to surface: no unread and no history to reveal. Render nothing so
  // the panel does not occupy space (or show an empty header) on records with
  // no notifications.
  if (unreadCount === 0 && !hasHistory) {
    return null;
  }

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      onExpandHistory?.();
    }
  };

  return (
    <div
      className={cn(
        "mb-4 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Bell className="h-3.5 w-3.5 text-[var(--ink-2)]" />
          <span className="text-[11px] font-medium text-[var(--ink)]">
            Notifications
          </span>
          {unreadCount > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--status-warn)] px-1 text-[10px] font-medium text-white">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onMarkAllRead}
              disabled={markAllPending}
              className="cursor-pointer gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </Button>
          )}
          {hasHistory && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={toggleExpanded}
              title={expanded ? "Hide read notifications" : "Show read notifications"}
              aria-label={
                expanded ? "Hide read notifications" : "Show read notifications"
              }
              aria-expanded={expanded}
              className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  expanded && "rotate-180",
                )}
              />
            </Button>
          )}
        </div>
      </div>

      {(unreadCount > 0 || (expanded && hasHistory)) && (
        <div className="flex flex-col gap-2 border-t-[0.5px] border-[var(--rule)] px-3 py-3">
          {unread.map((notification) => (
            <NotificationItem
              key={notification.uuid}
              notification={notification}
              muted={false}
              action={renderItemAction?.(notification)}
              onMarkRead={onMarkRead}
              onDismiss={onDismiss}
            />
          ))}
          {expanded &&
            read.map((notification) => (
              <NotificationItem
                key={notification.uuid}
                notification={notification}
                muted
                action={renderItemAction?.(notification)}
                onDismiss={onDismiss}
              />
            ))}
        </div>
      )}
    </div>
  );
}
